import React, { useEffect, useRef, useState } from 'react'
import TitleWithBackNavigation from '../../../../components/TitleWithBackNavigation'
import { Button, Col, Input, Row } from 'antd'
import { Controller, useForm } from 'react-hook-form'
import { useDispatch, useSelector } from 'react-redux'
import { yupResolver } from '@hookform/resolvers/yup'
import { businessInfoSchema } from './schema'
import LocationPicker from '../../../../components/LocationPicker'
import TextArea from 'antd/es/input/TextArea'
import CustomBenefits from './customBenefits'
import { ApiCaller, Colors } from '../../../../config'
import { setError, setMessage } from '../../../../store/common/generalSlice'
import { useMutation } from 'react-query'
import { useNavigate } from 'react-router-dom'
import { fetchProfileData } from '../../../../store/common/settingSlice'

const BusinessDetails = () => {

    const dispatch = useDispatch();
    const navigate = useNavigate();
    const currentFormStateRef = useRef(null);
    const currentBenefitsStateRef = useRef(null);

    const { profileData } = useSelector(state => state?.setting);

    // to get the coordinates from the location component as the onChange is only setting the
    // formatted address for the field
    const [coordinates, setCoordinates] = useState({
        businessLatitude: '',
        businessLongitude: ''
    })

    // a state to reset some of the custom field after submission of a form
    const [customFieldReset, setCustomFieldReset] = useState({
        address: false,
        time: false
    })

    const [customDirtyFieldTrack, setCustomDirtyFieldTrack] = useState(false);

    const schema = businessInfoSchema;
    const { control, handleSubmit, reset, formState: { isDirty, dirtyFields }, watch } = useForm({
        resolver: yupResolver(schema),
        mode: 'onChange'
    })

    const hasDirtyFieldsExcludingBenefits = Object.keys(dirtyFields).some((key) => key !== "benefits");

    // pre filled the form data in case of edit
    useEffect(() => {
        if (profileData && Object.keys(profileData)?.length > 0 && Object.keys(profileData?.userDetail)?.length > 0) {
            reset({
                businessName: profileData?.userDetail?.businessName ? profileData?.userDetail?.businessName : '',
                website: profileData?.userDetail?.website ? profileData?.userDetail?.website : '',
                businessContact: profileData?.userDetail?.businessContact ? profileData?.userDetail?.businessContact : '',
                aboutBusiness: profileData?.userDetail?.aboutBusiness ? profileData?.userDetail?.aboutBusiness : '',
                businessLocation: profileData?.userDetail?.businessLocation ? profileData?.userDetail?.businessLocation : '',
                benefits: profileData?.userDetail?.benefits ? JSON.parse(profileData?.userDetail?.benefits) : [],
            })
            currentBenefitsStateRef.current = profileData?.userDetail?.benefits ?? null;
            setCoordinates({
                businessLatitude: Number(profileData?.userDetail?.latitude),
                businessLongitude: Number(profileData?.userDetail?.longitude)
            })
        }
    }, [profileData])

    useEffect(() => {

        if (currentBenefitsStateRef?.current !== null) {

            const updatedBenefits = watch('benefits') || [];
            const originalBenefits = JSON.parse(currentBenefitsStateRef?.current || '[]');

            const isDifferent = updatedBenefits.length !== originalBenefits.length ||
                !updatedBenefits.every((benefit) => originalBenefits.includes(benefit))
            setCustomDirtyFieldTrack(isDifferent);
        }

    }, [watch('benefits')])


    const businessInfoMutation = useMutation(async (data) => {
        let res = await ApiCaller.put('/user/update-business-profile', data);
        return res
    },
        {
            onSuccess: (res) => {
                dispatch(setMessage(res?.data?.data?.message))
                dispatch(fetchProfileData())
                // setCustomFieldReset({ ...customFieldReset, address: true })
                // navigate('/settings')
            },

            onError: (error) => {
                dispatch(setError(error?.response?.data?.error?.message))
            }
        }
    )

    const submitForm = (data) => {
        const payload = {
            ...data,
            businessLatitude: coordinates?.businessLatitude,
            businessLongitude: coordinates?.businessLongitude,
            businessAddressLine1: "",
            businessAddressLine2: "",
            businessTown: "",
            businessPostalCode: ""
        }
        businessInfoMutation.mutate(payload)
    }

    return (
        <>
            <TitleWithBackNavigation title={'Business Info'} link={'/settings/businessInfo'} />
            <Row justify={'space-between'} className='my-4'>
                <Col lg={24} md={24} className='mb-4'>
                    <form>
                        <Row gutter={[6, 26]} justify={'space-between'}>
                            <Col lg={11} md={24} sm={24} xs={24} className='flex flex-wrap justify-between gap-3 items-center h-fit'>
                                <Controller
                                    name='businessName'
                                    control={control}
                                    defaultValue={''}
                                    render={({ field, fieldState }) => {
                                        return (
                                            <Col md={11} sm={24} xs={24}>
                                                <label className='font-medium'>Business name </label>
                                                <Input className='custom--input' {...field} type='text' />
                                                {fieldState.error && <p className='text-red-500'>{fieldState.error.message}</p>}
                                            </Col>
                                        )
                                    }}
                                />
                                <Controller
                                    name='website'
                                    control={control}
                                    defaultValue={''}
                                    render={({ field, fieldState }) => {
                                        return (
                                            <Col md={11} sm={24} xs={24}>
                                                <label className='font-medium'>Website </label>
                                                <Input className='custom--input' {...field} type='text' />
                                                {fieldState.error && <p className='text-red-500'>{fieldState.error.message}</p>}
                                            </Col>
                                        )
                                    }}
                                />
                                <Controller
                                    name='businessContact'
                                    control={control}
                                    defaultValue={''}
                                    render={({ field, fieldState }) => {
                                        return (
                                            <Col md={11} sm={24} xs={24}>
                                                <label className='font-medium'>Business contact number </label>
                                                <Input className='custom--input' {...field} type='text' placeholder='Enter Phone Number' />
                                                {fieldState.error && <p className='text-red-500'>{fieldState.error.message}</p>}
                                            </Col>
                                        )
                                    }}
                                />
                                <Controller
                                    name='businessLocation'
                                    control={control}
                                    defaultValue={''}
                                    render={({ field: { value, onChange }, fieldState }) => {
                                        return (
                                            <Col md={11} sm={24} xs={24}>
                                                <label className='font-medium'>Business Location </label>
                                                <LocationPicker
                                                    value={value}
                                                    isBusinessInfo={true}
                                                    addressPicker={(coordinates, address) => {
                                                        onChange(address)
                                                        setCoordinates({ businessLatitude: coordinates?.lat, businessLongitude: coordinates?.lng })
                                                    }}
                                                    onEdit={() => { }}
                                                    customFieldReset={customFieldReset.address}
                                                    setCustomFieldReset={setCustomFieldReset}
                                                    coordinates={coordinates}
                                                />
                                                {fieldState.error && <p className='text-red-500'>{fieldState.error.message}</p>}
                                            </Col>
                                        )
                                    }}
                                />
                                <Controller
                                    name='aboutBusiness'
                                    control={control}
                                    defaultValue={''}
                                    render={({ field, fieldState }) => {
                                        return (
                                            <Col md={24} sm={24} xs={24}>
                                                <label className='font-medium'>About Business </label>
                                                <TextArea autoSize={{ minRows: 4, maxRows: 4 }} className='custom--input' rows={4} {...field} />
                                                {fieldState.error && <p className='text-red-500'>{fieldState.error.message}</p>}
                                            </Col>
                                        )
                                    }}
                                />

                                <Button
                                    onClick={handleSubmit((data) => submitForm(data))}
                                    loading={businessInfoMutation.isLoading}
                                    disabled={!hasDirtyFieldsExcludingBenefits && !customDirtyFieldTrack}
                                    htmlType='submit'
                                    className='hidden lg:block w-36 h-10 mt-5 ml-1 rounded-md outline-none border-none'
                                    style={{ background: Colors.AppYellow, color: 'white' }}>
                                    Save
                                </Button>
                            </Col>

                            <Col lg={11} md={24} sm={24} xs={24} className='overflow-y-auto max-h-[500px] pr-2'>
                                <Controller
                                    name='benefits'
                                    control={control}
                                    defaultValue={''}
                                    render={({ field: { value, onChange }, fieldState }) => {
                                        return (
                                            <Col lg={24} md={12} sm={24} xs={24}>
                                                <label className='font-medium'>Benefits: </label>
                                                <CustomBenefits value={value || []} onChange={(data) => {
                                                    onChange(data)
                                                }} />

                                            </Col>
                                        )
                                    }}
                                />
                            </Col>
                        </Row>

                        <Button
                            onClick={handleSubmit((data) => submitForm(data))}
                            loading={businessInfoMutation.isLoading}
                            htmlType='submit'
                            disabled={!hasDirtyFieldsExcludingBenefits && !customDirtyFieldTrack}
                            className='block lg:hidden w-36 h-10 mt-5 rounded-md outline-none border-none'
                            style={{ background: Colors.AppYellow, color: 'white' }}>
                            Save
                        </Button>

                    </form>

                </Col>
            </Row>
        </>
    )
}

export default BusinessDetails